'use client';

import * as React from 'react';
import { DataTable } from '@/components/data-table/data-table';
import { useDataTable } from '@/hooks/use-data-table';
import { DataTableSortList } from '@/components/data-table/data-table-sort-list';

import { useUpsellsTableColumns } from './upsells-table-columns';
import { UpsellsTableActions } from './UpsellsTableActions';
import { UpsellsTableActionBar } from './UpsellsTableActionBar';
import { UpsellsTableSkeleton } from './UpsellsTableSkeleton';
import { MainTableState } from '@/lib/utils/table-state-utils';
import type { TableConfig } from '@/types/data-table';
import { DEFAULT_SKELETON_ROWS } from '../../../../../config/data-table';
import { DeleteUpsellModal } from './delete-upsell-modal';
import { UpsellItem } from '@/lib/api/upsells';
import { useStoreNavigate } from '@/hooks/use-app-navigate';

interface UpsellsTableProps {
    upsells: UpsellItem[];
    total: number;
    loading: boolean;
    onTableStateChange: (state: MainTableState) => void;
    onRefetch: () => void;
    config?: TableConfig;
}

/**
 * UpsellsTable component for displaying upsells with DataTable functionality
 */
export function UpsellsTable({
    upsells,
    total,
    loading,
    onTableStateChange,
    onRefetch,
    config = {},
}: UpsellsTableProps) {

    const { id, navigate, storeRoutes } = useStoreNavigate();
    const [upsellId, setUpsellId] = React.useState<string | undefined>(undefined);

    const columns = useUpsellsTableColumns({
        storeId: id,
        onEditUpsell: (id: string) => navigate(storeRoutes.UPSELL_EDIT(id)),
        onDeleteUpsell: (id: string) => setUpsellId(id),
    });

    const { table } = useDataTable({
        data: upsells,
        columns,
        total,
        config,
        onTableStateChange,
        getRowId: (row: UpsellItem) => row.id,
    });

    return (
        <DataTable
            table={table}
            actionBar={<UpsellsTableActionBar table={table} storeId={id} />}
            isLoading={loading}
            skeletonRows={<UpsellsTableSkeleton rows={config.skeletonRows || DEFAULT_SKELETON_ROWS} />}
        >
            <UpsellsTableActions table={table} onRefresh={onRefetch} isLoading={loading} storeId={id} />
            <DataTableSortList table={table} align="end" />
            <DeleteUpsellModal upsellId={upsellId} storeId={id} onClose={() => setUpsellId(undefined)} />
        </DataTable>
    );
}
